import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogisticsStatusBadge, LogisticsTypeBadge } from '@/modules/logistics/components';
import { getLogisticsOrder } from '@/modules/logistics/services';
import { getReservation } from '@/modules/reservations/services';
import { buildLogisticsOrderNewUrl } from '@/modules/logistics/utils/logisticsUrl';
import type { LogisticsOrder } from '@/modules/logistics/types';
import type { CommercialOperation } from '@/modules/commercial/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type OperationLinksPanelProps = {
  operation: CommercialOperation;
};

export function OperationLinksPanel({ operation }: OperationLinksPanelProps) {
  const { hasPermission } = useAuth();
  const canReadRes = hasPermission('reservations.read');
  const canReadLog = hasPermission('logistics.read');
  const canWriteLog = hasPermission('logistics.write');

  const [reservationTitle, setReservationTitle] = useState<string | null>(null);
  const [logisticsOrders, setLogisticsOrders] = useState<LogisticsOrder[]>([]);

  useEffect(() => {
    if (operation.reservationId && canReadRes) {
      void getReservation(operation.reservationId).then((r) =>
        setReservationTitle(r?.title ?? null),
      );
    } else {
      setReservationTitle(null);
    }
  }, [operation.reservationId, canReadRes]);

  useEffect(() => {
    if (!canReadLog || operation.logisticsOrderIds.length === 0) {
      setLogisticsOrders([]);
      return;
    }
    void Promise.all(
      operation.logisticsOrderIds.map((id) => getLogisticsOrder(id)),
    ).then((rows) => setLogisticsOrders(rows.filter((o): o is LogisticsOrder => o !== null)));
  }, [operation.logisticsOrderIds, canReadLog]);

  return (
    <div className="inventory-panel">
      <h2 className="inventory-panel__subtitle">Vínculos</h2>

      <h3>Reserva</h3>
      {operation.reservationId && canReadRes ? (
        <p>
          <Link to={`${modulePaths.reservations}/${operation.reservationId}`}>
            {reservationTitle ?? operation.reservationId}
          </Link>
        </p>
      ) : (
        <p className="auth-muted">
          {operation.type === 'sale'
            ? 'No aplica reserva para ventas.'
            : 'Sin reserva asociada.'}
        </p>
      )}

      {canReadLog ? (
        <>
          <div className="inventory-page-header">
            <h3>Logística</h3>
            {canWriteLog && operation.reservationId ? (
              <Link
                to={buildLogisticsOrderNewUrl({
                  reservationId: operation.reservationId,
                  type: 'delivery',
                })}
                className="auth-button auth-button--secondary inventory-page-header__btn"
              >
                + Orden logística
              </Link>
            ) : null}
          </div>
          {logisticsOrders.length === 0 ? (
            <p className="auth-muted">Sin órdenes logísticas vinculadas.</p>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logisticsOrders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <LogisticsTypeBadge type={o.type} />
                    </td>
                    <td>{o.scheduledDate}</td>
                    <td>
                      <LogisticsStatusBadge status={o.status} />
                    </td>
                    <td>
                      <Link
                        to={`${modulePaths.logistics}/${o.id}`}
                        className="link-button"
                      >
                        Ver
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </div>
  );
}
